import React, { useRef, useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { InputFieldOTP } from "../../components/ui/Inputs";

// --- Interfaces ---
interface OtpInputFormProps {
  emailMasked?: string;
  mobileMasked?: string;
  otpLength?: number;
  onOtpSubmit: (otp: string) => void;
  onResendOtp: () => void;
  initialTimerSeconds?: number;
  errorMessage?: string;
  setErrorMsg?: any;
  showConsentMessage?: boolean;
}

const OtpInputForm: React.FC<OtpInputFormProps> = ({
  emailMasked,
  mobileMasked,
  otpLength = 6,
  onOtpSubmit,
  onResendOtp,
  initialTimerSeconds = 180,
  errorMessage = "",
  setErrorMsg,
  showConsentMessage = false
}) => {
  const [otp, setOtp] = useState<string[]>(new Array(otpLength).fill(""));
  const [timer, setTimer] = useState(initialTimerSeconds);
  const [canResend, setCanResend] = useState(false);
  const [showPurposesContent, setShowPurposesContent] = useState(false); // will be used only when showConsentMessage is true CUSJOUR-279
  const inputRefs = useRef<HTMLInputElement[]>([]);

  // --- Timer logic ---
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
      return () => clearInterval(interval);
    } else {
      setCanResend(true);
    }
  }, [timer]);

  const formatTime = (sec: number) => {
    const minutes = Math.floor(sec / 60)
      .toString()
      .padStart(2, "0");
    const seconds = (sec % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  const handleResend = () => {
    setOtp(new Array(otpLength).fill(""));
    setCanResend(false);
    setTimer(initialTimerSeconds);
    inputRefs.current[0]?.focus();
    onResendOtp();
  };

  const handleChange = (element: HTMLInputElement, index: number) => {
    const value = element.value;

    if (!/^\d*$/.test(value)) {
      return;
    } else {
      const singleCharValue = value.slice(-1);
      const newOtp = [...otp];
      newOtp[index] = singleCharValue;
      setOtp(newOtp);

      if (singleCharValue !== "" && index < otp.length - 1) {
        inputRefs.current[index + 1]?.focus();
      } else if (singleCharValue === "" && index > 0) {
      }
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && otp[index] === "" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = () => {
    const fullOtp = otp.join("");
    if (otp.every((d) => d !== "")) {
      onOtpSubmit(fullOtp);
    } else {
      alert("Please enter all digits of the OTP.");
    }
  };

  const isMobilePurposesExpanded = showConsentMessage && showPurposesContent;

  return (
    <Box
      sx={{
        display: { xs: isMobilePurposesExpanded ? "flex" : "flex", sm: "flex" },
        flexDirection: "column",
        height: showConsentMessage
          ? { xs: isMobilePurposesExpanded ? "72vh" : "72vh", sm: "72vh" }
          : { xs: "36vh", sm: "36vh" },
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        {/* Description text only for provided values */}
        <Typography variant="body1" color="text.secondary" mb={2}>
          Please enter the OTP sent to{" "}
          {emailMasked && (
            <>
              your email ID
              {/* <Box component="span" fontWeight={600} color="#00b3b5">
                {emailMasked}
              </Box> */}
            </>
          )}
          {emailMasked && mobileMasked && " and "}
          {mobileMasked && (
            <>
              your mobile number
              {/* <Box component="span" fontWeight={600} color="#00b3b5">
                {mobileMasked}
              </Box> */}
            </>
          )}
          .
        </Typography>

        {/* OTP Fields */}
        <Box display="flex" justifyContent="start" gap={2} mb={2}>
          {otp.map((digit, index) => (
            <InputFieldOTP
              key={index}
              width="40px"
              inputRef={(el) =>
                (inputRefs.current[index] = el as HTMLInputElement)
              }
              type="tel"
              inputMode="decimal"
              autoComplete="one-time-code"
              value={digit}
              onChange={(e) => {
                handleChange(e.target as HTMLInputElement, index);
                setErrorMsg?.("");
              }}
              onKeyDown={(e: any) => handleKeyDown(e, index)}
              inputProps={{ maxLength: 1 }}
            />
          ))}
        </Box>

        {/* Timer and Resend */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={errorMessage ? 1 : 2}
        >
          <Typography variant="body2" fontWeight={500} color="#616161">
            {formatTime(timer)}
          </Typography>
          <Button
            variant="text"
            disabled={!canResend}
            onClick={handleResend}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: canResend ? "#00b3b5" : "#bdbdbd",
              fontSize: "0.9rem",
            }}
          >
            Resend OTP
          </Button>
        </Box>

        {errorMessage ? (
          <Typography
            sx={{
              fontSize: "12px",
              color: "red",
              mb: showConsentMessage ? 2 : 0,
            }}
          >
            {errorMessage}
          </Typography>
        ) : (
          <></>
        )}
      </Box>

      {showConsentMessage ? (
        <Box
          sx={{
            flex: isMobilePurposesExpanded ? 1 : "unset",
            overflowY: { xs: isMobilePurposesExpanded ? "auto" : "auto", sm: "auto" },
            pr: { xs: isMobilePurposesExpanded ? 0.5 : 0, sm: 0 },
            mb: isMobilePurposesExpanded ? 1 : 0,
          }}
        >
          <Typography sx={{ fontSize: "14px", fontWeight: "400", color: "#00000080" }}>
            By providing OTP, you expressly consent Avanse Financial Services Ltd
            ("AFSL"), its authorized representative/third parties (which includes our
            vendors, service providers, recovery agents, etc.), regulators or
            governmental authorities, credit information companies, KYC registration
            and authentication service agencies, etc., to collect, handle, store,
            use, disclose, download, share and process your data, including personal
            data for the
            <span
              style={{
                cursor: "pointer",
                marginLeft: "5px",
                color: "#1CB1B1",
                fontWeight: "500",
              }}
              onClick={() => setShowPurposesContent(!showPurposesContent)}
            >
              purposes
            </span>
            .
          </Typography>

          {showPurposesContent ? (
            <ol style={{
              fontSize: "14px",
              fontWeight: "400",
              color: "#00000080",
              paddingLeft: 0,
              marginLeft: 0,
              listStylePosition: "outside"
            }}>
              <li><span style={{marginRight:4}}>1.</span>For relying upon KYC documents supplied to us, whether by you or through our representative/service provider, for meeting our legal, regulatory and compliance obligations such as customer due diligence, know your customer/re-KYC/anti-money laundering checks, processing your loan application details and other value-added services</li>
              <li><span style={{ marginRight:4}}>2.</span>For providing your application details to Central KYC Registry, UIDAI, credit information companies/credit bureaus for verification purposes</li>
              <li><span style={{marginRight:4}}>3.</span>For verifying the applied (i) course or admission details with the institute/university, (ii) credentials with the relevant third parties required in respect of the loan application, (iii) enrollment verifications from organizations/entity in possession of such data</li>
              <li><span style={{marginRight:4}}>4.</span>For enabling repayment and ensuring recovery of amounts due from you including through our appointed recovery agents, etc.</li>
              <li><span style={{ marginRight:4}}>5.</span>For our general administration and granting services in relation to the above</li>
              <li><span style={{marginRight:4}}>6.</span>For contacting you over, Emails, WhatsApp, call and SMS, overriding NDNC registration.</li>
            </ol>
          ) : (
            <></>
          )}
        </Box>
      ) : (
        <></>
      )}

      {/* Submit Button */}
      <Box
        display="flex"
        justifyContent="center"
        mt={errorMessage ? 3 : 2}
        sx={{
          flexShrink: 0,
          position: { xs: isMobilePurposesExpanded ? "sticky" : "sticky", sm: "sticky" },
          bottom: 0,
          backgroundColor: { xs: isMobilePurposesExpanded ? "#fff" : "transparent", sm: "transparent" },
          pt: { xs: isMobilePurposesExpanded ? 1 : 0, sm: 0 },
          zIndex: 1,
        }}
      >
        <Box width={{ xs: "100%", sm: "50%" }}>
          <Button
            variant="contained"
            fullWidth
            disabled={!otp.every((d) => d !== "")}
            onClick={handleSubmit}
            sx={{
              textTransform: "none",
              bgcolor: "#00b3b5",
              color: "#fff",
              fontWeight: 600,
              fontSize: "1rem",
              borderRadius: "8px",
              py: 1.2,
              "&:hover": {
                bgcolor: "#00999a",
              },
              "&.Mui-disabled": {
                bgcolor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            Continue
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default OtpInputForm;